import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/AppHeader";
import BottomNavigation from "@/components/BottomNavigation";
import { QrCode, ExternalLink, ChevronLeft, ChevronRight, Loader2, UserPlus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/redesign";
import { toast } from "@/hooks/use-toast";
import { projectsAPI } from "@/api/projects";
import { Capacitor } from "@capacitor/core";
import { Separator } from "@/components/ui/separator";
import { isNativePlatform, shouldDisableFeature } from "@/utils/platform";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { CapacitorBarcodeScanner } from '@capacitor/barcode-scanner';
import { supabase } from "@/integrations/supabase/client";
import { formatInDeviceTimezone, adjustDateFromDB } from "@/utils/dateUtils";

type Project = {
  id: string;
  name: string;
};

type SharedProject = {
  id: string;
  name?: string;
  role: "owner" | "editor" | "viewer";
  linked_via?: string;
  created_at?: string;
  display_timestamp?: string;
  owner?: {
    id: string;
    name?: string;
    email: string;
  };
  user?: {
    id: string;
    name?: string;
    email: string;
  };
};

const scannerStyles = `
body.scanner-active {
  visibility: hidden !important;
  opacity: 0 !important;
  position: fixed !important;
  width: 100% !important;
  height: 100% !important;
  top: 0 !important;
  left: 0 !important;
  z-index: -1 !important;
}

/* Container used by the plugin - might not be needed for styling */
.scanner-container {
  position: fixed !important;
  width: 100% !important;
  height: 100% !important;
  top: 0 !important;
  left: 0 !important;
  z-index: 9999 !important;
  background: transparent !important;
}
`;

const PaylasimPage: React.FC = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProject, setActiveProject] = useState<string>("");

  const [sharedProjects, setSharedProjects] = useState<SharedProject[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);

  const [isScanning, setIsScanning] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [scannedUserId, setScannedUserId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<'viewer' | 'editor'>('viewer');
  const [isAddingMember, setIsAddingMember] = useState(false);

  const [activeTabValue, setActiveTabValue] = useState<string>("manageMembers");

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = scannerStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
      if (document.body.classList.contains('scanner-active')) {
        document.body.classList.remove('scanner-active');
      }
    };
  }, []);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsData = await projectsAPI.getProjects();
        setProjects(projectsData);
        
        if (projectsData.length > 0) {
          setActiveProject(projectsData[0].id);
        }
      } catch (error) {
        
        toast({
          title: t('common.error'),
          description: t('sharing.projects_load_error'),
          variant: "destructive",
        });
      }
    };
    
    loadProjects();
  }, [t]);
  
  useEffect(() => {
    if (activeProject) {
      loadProjectMembers(activeProject);
    }
  }, [activeProject]);

  const handleTabChange = (value: string) => {
    setActiveTabValue(value);
  };

  const loadProjectMembers = async (projectId: string) => {
    setIsLoadingMembers(true);
    try {
      const members = await projectsAPI.getProjectMembers(projectId);
      
      
      // Log the created_at field for each member to verify it's coming through correctly
      members.forEach(member => {
        if (member.user) {
          
        }
      });
      
      setSharedProjects(members);
    } catch (error) {
      
      toast({
        title: t('common.error'),
        description: t('sharing.members_load_error'),
        variant: "destructive",
      });
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const handleRemoveMember = async (projectId: string, userIdToRemove: string) => {
    setIsRemovingMember(true);
    try {
      await projectsAPI.removeProjectMember(projectId, userIdToRemove);
      
      toast({
        title: t('common.success'),
        description: t('sharing.member_remove_success'),
      });
      
      loadProjectMembers(projectId);
    } catch (error: any) {
      
      toast({
        title: t('common.error'),
        description: error.message || t('sharing.member_remove_error'),
        variant: "destructive",
      });
    } finally {
      setIsRemovingMember(false);
    }
  };

  const formatShareMethod = (method: string | undefined | null) => {
    if (!method) return "";
    
    switch (method.toLowerCase()) {
      case 'share':
        return t('sharing.added_via_qr');
      case 'self':
        return t('sharing.added_via_self');
      default:
        return method;
    }
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return "N/A";
    
    try {
      // Parse ISO date string to ensure it's valid
      const timestamp = Date.parse(dateString);
      
      // Check if the date is valid
      if (isNaN(timestamp)) {
        
        return t('sharing.invalid_date');
      }
      
      // Use our timezone-aware utilities to display the date in the device's timezone
      return formatInDeviceTimezone(
        adjustDateFromDB(dateString),
        "dd.MM.yyyy HH:mm"
      );
    } catch (error) {
      
      return t('sharing.date_error');
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return <StatusBadge tone="brand">{t('sharing.role_owner')}</StatusBadge>;
      case 'editor':
        return <StatusBadge tone="success">{t('sharing.role_editor')}</StatusBadge>;
      case 'viewer':
        return <StatusBadge tone="warning">{t('sharing.role_viewer')}</StatusBadge>;
      default:
        return <StatusBadge tone="neutral">{role}</StatusBadge>;
    }
  };

  const handleScanUserQR = async () => {
    if (!isNativePlatform() || shouldDisableFeature('camera')) {
      toast({ title: t('common.info'), description: t('sharing.qr_mobile_only') });
      return;
    }
    
    if (!activeProject) {
       toast({ title: t('common.error'), description: t('sharing.select_project_first'), variant: "destructive" });
       return;
    }

    // Simple connectivity check before scanning
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({ title: t('common.error'), description: t('sharing.session_verification_failed'), variant: "destructive" });
        return;
      }
    } catch (connError) {
      
      toast({ title: t('errors.connection_error'), description: t('sharing.connection_error'), variant: "destructive" });
      return;
    }

    setScannedUserId(null);
    setIsRoleModalOpen(false);
    setIsScanning(true);
    document.body.classList.add('scanner-active');

    try {
      const result = await CapacitorBarcodeScanner.scanBarcode({
        hint: 17,
        scanInstructions: t('sharing.scan_qr_to_add')
      });

      document.body.classList.remove('scanner-active');
      setIsScanning(false);

      const scannedUserId = result.ScanResult;

      if (scannedUserId && scannedUserId.trim() !== '') {
        
        if (/^[a-fA-F0-9-]{36}$/.test(scannedUserId)) {
          setScannedUserId(scannedUserId);
          setSelectedRole('viewer');
          setIsRoleModalOpen(true);
        } else {
          toast({ title: t('sharing.invalid_qr_code'), description: t('sharing.invalid_qr_message'), variant: "destructive"});
        }
      } else {
        
        toast({ title: t('sharing.scan_cancelled'), description: t('sharing.scan_cancelled_message') });
      }

    } catch (error: any) {
      

      document.body.classList.remove('scanner-active');
      setIsScanning(false);

      let message = t('sharing.qr_read_error');
      if (error.message?.includes("user did not grant permission") || 
          error.message?.includes("permission")) {
        message = t('sharing.camera_permission_denied');
      } else if (error.message?.includes("cancelled") || 
                 error.message?.includes("canceled")) {
        message = t('sharing.scan_cancelled_message');
      }
      
      toast({ title: t('sharing.scan_error'), description: message, variant: "destructive"});
    }
  };

  const handleConfirmLinkUser = async () => {
    if (!scannedUserId || !activeProject) {
      toast({ title: t('common.error'), description: t('sharing.user_project_info_missing'), variant: "destructive"});
      return;
    }

    setIsAddingMember(true);
    try {
      // First verify network connectivity
      if (Capacitor.isNativePlatform()) {
        try {
          // Simple connectivity check before proceeding
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            throw new Error(t('sharing.session_verification_failed'));
          }
          
        } catch (connError) {
          
          throw new Error(t('sharing.connection_error'));
        }
      }

      
      
      try {
        // Add member with timeout handling
        const addMemberPromise = projectsAPI.addProjectMember(activeProject, scannedUserId, selectedRole);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error(t('sharing.timeout_error'))), 20000)
        );
        
        const result = await Promise.race([addMemberPromise, timeoutPromise]);
        
        
        toast({ title: t('common.success'), description: t('sharing.member_added_successfully', { 
          role: selectedRole === 'editor' ? t('sharing.role_editor_label') : t('sharing.role_viewer_label')
        })});
        setIsRoleModalOpen(false);
        setScannedUserId(null);
        loadProjectMembers(activeProject);
      } catch (apiError) {
        
        
        // Handle empty error object specifically
        if (!apiError || (typeof apiError === 'object' && Object.keys(apiError).length === 0)) {
          
          throw new Error(t('sharing.connection_error'));
        }
        
        throw apiError; // Re-throw for the outer catch block
      }
    } catch (error: any) {
      
      
      // Provide more detailed error feedback
      let errorMessage = t('sharing.add_member_error');
      
      if (!error) {
        errorMessage = t('sharing.connection_error');
      } else if (typeof error === 'object' && Object.keys(error).length === 0) {
        errorMessage = t('sharing.server_no_response');
      } else if (error.message?.includes("network") || error.message?.includes("internet") || 
          error.message?.includes("connection") || error.message?.includes("bağlantı")) {
        errorMessage = t('sharing.connection_error');
      } else if (error.message?.includes("already a member") || error.message?.includes("zaten projeye eklenmiş")) {
        errorMessage = t('sharing.already_member');
      } else if (error.message?.includes("timeout") || error.message?.includes("zaman aşımı")) {
        errorMessage = t('sharing.timeout_error');
      } else if (error.message?.includes("not found") || error.message?.includes("bulunamadı")) {
        errorMessage = t('sharing.user_not_found');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      
      toast({ title: t('common.error'), description: errorMessage, variant: "destructive"});
      setIsRoleModalOpen(false);
    } finally {
      setIsAddingMember(false);
    }
  };

  const getProjectName = (projectId: string) => {
    return projects.find(p => p.id === projectId)?.name || t('sharing.unknown_project');
  };

  return (
    <div className="page-container bg-gray-25">
      <div>
        <AppHeader title={t('sharing.project_access')} />

        <main className="main-content">
          <div className="px-4 pb-4 space-y-4">
            <Tabs
              defaultValue="manageMembers"
              value={activeTabValue}
              onValueChange={handleTabChange}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-1 mb-4 bg-gray-100 border border-gray-200 rounded-md p-1">
                <TabsTrigger value="manageMembers" className="font-semibold text-sm data-[state=active]:bg-brand-600 data-[state=active]:text-white rounded-md">
                  {t('sharing.project_members')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="manageMembers" className="space-y-4 data-[state=active]:animate-in">
                {/* QR scan on native platforms */}
                {isNativePlatform() && !shouldDisableFeature('camera') && (
                  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-3">
                    <p className="text-[15px] font-bold text-gray-900">{t('sharing.add_new_member')}</p>
                    <p className="text-[12px] text-gray-500">{t('sharing.scan_qr_description')}</p>
                    <Button
                      onClick={handleScanUserQR}
                      className="w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold"
                      disabled={isScanning || !activeProject}
                    >
                      {isScanning ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('sharing.scanning')}</>
                      ) : (
                        <><QrCode className="mr-2 h-4 w-4" /> {t('sharing.scan_user_qr')}</>
                      )}
                    </Button>
                    {!activeProject && (
                      <p className="text-[11px] text-error-600 text-center">{t('sharing.select_project_first')}</p>
                    )}
                  </div>
                )}

                {/* Web alternative */}
                {(!isNativePlatform() || shouldDisableFeature('camera')) && (
                  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-3">
                    <p className="text-[15px] font-bold text-gray-900">{t('sharing.add_new_member')}</p>
                    <p className="text-[12px] text-gray-500">{t('sharing.qr_only_mobile')}</p>
                    <div className="text-center py-3 text-gray-400">
                      <QrCode className="mx-auto h-8 w-8 mb-2 opacity-40" />
                      <p className="text-[13px]">{t('sharing.mobile_only_feature')}</p>
                    </div>
                  </div>
                )}

                {/* Members list */}
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-4">
                  <p className="text-[15px] font-bold text-gray-900">{t('sharing.project_members')}</p>
                  <div className="space-y-1.5">
                    <label htmlFor="manage-project-select" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
                      {t('sharing.project_to_manage')}
                    </label>
                    <Select value={activeProject} onValueChange={setActiveProject} disabled={isScanning}>
                      <SelectTrigger id="manage-project-select" className="bg-white border-gray-200 text-gray-900 rounded-md">
                        <SelectValue placeholder={t('sharing.select_project')} />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 rounded-md">
                        {projects.map(project => (
                          <SelectItem key={project.id} value={project.id} className="focus:bg-brand-50 text-gray-700">
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {isLoadingMembers ? (
                    <div className="flex justify-center items-center py-6">
                      <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
                    </div>
                  ) : sharedProjects.length > 0 ? (
                    <ul className="space-y-2">
                      {sharedProjects.map((member) => (
                        <li key={member.user?.id || member.id} className="flex items-center justify-between p-3 bg-gray-25 border border-gray-100 rounded-xl">
                          <div className="min-w-0 mr-3">
                            <p className="text-[14px] font-semibold text-gray-900 truncate">
                              {member.user?.name || member.user?.email || t('sharing.unknown_user')}
                            </p>
                            <p className="text-[12px] text-gray-500 truncate">{member.user?.email}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-1.5">
                              {getRoleBadge(member.role)}
                              <div className="text-[11px] text-gray-400 flex flex-col">
                                {member.linked_via && (
                                  <span>{t('sharing.added_via')} {formatShareMethod(member.linked_via)}</span>
                                )}
                                {member.created_at && (
                                  <span>{t('sharing.added_date')} {formatDate(member.created_at)}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          {member.role !== 'owner' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0 border-error-100 text-error-600 hover:bg-error-50 text-[12px]"
                              onClick={() => member.user?.id && handleRemoveMember(activeProject, member.user.id)}
                              disabled={isRemovingMember || isScanning}
                            >
                              {isRemovingMember ? <Loader2 className="h-4 w-4 animate-spin" /> : t('sharing.removing')}
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-[13px] text-gray-500 text-center py-6">{t('sharing.no_members_yet')}</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </main>

        <BottomNavigation />
      </div>

      <Dialog open={isRoleModalOpen} onOpenChange={setIsRoleModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sharing.add_user')}</DialogTitle>
            <DialogDescription>
              "{getProjectName(activeProject)}" {t('sharing.assign_role_to_user')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-[13px] text-gray-600">
              {t('sharing.added_user_id')}{" "}
              <span className="font-mono bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-[12px]">{scannedUserId}</span>
            </p>
            <div className="space-y-1.5">
              <label htmlFor="role-select" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
                {t('sharing.assign_role')}
              </label>
              <Select
                value={selectedRole}
                onValueChange={(value) => setSelectedRole(value as 'viewer' | 'editor')}
              >
                <SelectTrigger id="role-select" className="bg-white border-gray-200 text-gray-900 rounded-md">
                  <SelectValue placeholder={t('sharing.select_role')} />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200 rounded-md">
                  <SelectItem value="viewer" className="focus:bg-brand-50 text-gray-700">{t('sharing.viewer_readonly')}</SelectItem>
                  <SelectItem value="editor" className="focus:bg-brand-50 text-gray-700">{t('sharing.editor_readwrite')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={isAddingMember} className="border-gray-200 text-gray-700">{t('common.cancel')}</Button>
            </DialogClose>
            <Button
              onClick={handleConfirmLinkUser}
              disabled={isAddingMember}
              className="bg-brand-600 hover:bg-brand-700 text-white font-semibold"
            >
              {isAddingMember ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
              {isAddingMember ? t('sharing.adding_member') : t('sharing.confirm_add_user')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default PaylasimPage; 